/**
 * The control bar's connection indicator.
 *
 * This used to be a `Math.random()` simulator — it seeded plausible numbers and
 * then invented new ones every few seconds, with a comment that said so. It fed
 * the always-visible control bar, so every user in every call was shown a
 * reading that had never touched the connection. The real measurements existed
 * the whole time, one module away.
 *
 * All the logic lives in `connection-quality.ts` so it can be tested; this is
 * just the React seam.
 */
import { useEffect, useState } from "react"
import {
  subscribeConnectionQuality,
  UNKNOWN,
  type ConnectionStats,
  type ConnectionQuality,
} from "@/features/meeting/services/connection-quality"

/** Kept as the exported name so existing consumers need no change. */
export type NetworkStats = ConnectionStats
export type { ConnectionQuality }

export function useNetworkQuality(): ConnectionStats {
  // Starts 'unknown' with nulls, not a plausible guess. The indicator shows
  // nothing until there is something real to show.
  const [stats, setStats] = useState<ConnectionStats>(UNKNOWN)
  useEffect(() => subscribeConnectionQuality(setStats), [])
  return stats
}
